import S, { DataSignal } from 's-js';
import SArray, { SDataArray } from 's-array';
import { IAjax } from 'lib/Ajax';
import { DataCacheCollection } from 'lib/DataCache';
import uniqueIndex from 'lib/uniqueIndex';
import { Entrypoints } from 'core/app/entrypoints';
import { LloydServiceSearchOptions } from 'data/models/LloydServiceSearch';
import { LloydServiceVessel } from 'data/models/LloydServiceVessel';
import { MarineTrafficServiceSearchOptions } from 'data/models/MarineTrafficServiceSearch';
import { MarineTrafficServiceVessel } from 'data/models/MarineTrafficServiceVessel';
import { PSixServiceSearchOptions } from 'data/models/PSixServiceSearch';
import { PSixServiceVessel } from 'data/models/PSixServiceVessel';
import { Vessel } from 'data/models/Vessel';
import { VesselServiceConfigureData } from 'data/models/VesselServiceConfigure';
import { VesselServiceSettings } from 'data/models/VesselServiceSettings';
import { VesselServiceConfigureCache } from './cache';

export interface ISearchModel<TServiceVessel> {
    IsSearching : DataSignal<boolean>;
    Results : () => ISearchResult<TServiceVessel>[];
    Search : () => void;
    SelectedResultId : DataSignal<number | null>;
    CanSearch : () => boolean;
    Link : () => void;
    SelectCurrentVessel : () => void;
    SelectedIdIsCurrent : () => boolean;
    CurrentVesselInResults : () => boolean;
}

export interface ISearchResult<TServiceVessel> {
    Model : TServiceVessel;
    SearchResultWeight : () => number;
}

export interface LloydSearchModel extends ISearchModel<LloydServiceVessel> {
    IMO : DataSignal<number | null>;
    MMSID : DataSignal<number | null>;
    CallSign: DataSignal<string>;
}

export interface MarineTrafficSearchModel extends ISearchModel<MarineTrafficServiceVessel> {
    IMO : DataSignal<number | null>;
    MMSID : DataSignal<number | null>;
}

export interface PSixSearchModel extends ISearchModel<PSixServiceVessel> {
    VesselName : DataSignal<string>;
    CallSign : DataSignal<string>;
    HIN : DataSignal<string>;
    VIN : DataSignal<string>;
}

export interface VesselServiceConfigureParams {
    id : number;
}

export function VesselServiceConfigure (
    ajax : IAjax,
    entrypoints : Entrypoints,
    cache : VesselServiceConfigureCache,
    vesselServiceSettings : VesselServiceSettings,
    { id } : VesselServiceConfigureParams
) {
    const model = cache.VesselServiceConfigures.update1(null, { VesselId: id }),
        request = ajax.Url<VesselServiceConfigureData>(entrypoints.VesselServiceConfigure({ Id: id })).Get(),
        lloydSearch = LloydSearchModel(),
        marineTrafficSearch = MarineTrafficSearchModel(),
        pSixSearch = PSixSearchModel(),
        selectedDuplicateVesselId = S.data<number | null>(null);

    S(() => {
        const vessel = model.Vessel();
        if (!vessel) return;

        // Populate search terms from vessel info
        S(() => {
            if (vessel.IMO()) {
                const imo = +vessel.IMO()!;
                if (!isNaN(imo)) {
                    if (!S.sample(marineTrafficSearch.IMO)) marineTrafficSearch.IMO(imo);
                    if (!S.sample(lloydSearch.IMO)) lloydSearch.IMO(imo);
                }
            }
        });

        S(() => {
            if (vessel.MMSID()) {
                const mmsid = +vessel.MMSID()!;
                if (!isNaN(mmsid)) {
                    if (!S.sample(marineTrafficSearch.MMSID)) marineTrafficSearch.MMSID(mmsid);
                    if (!S.sample(lloydSearch.MMSID)) lloydSearch.MMSID(mmsid);
                }
            }
        });

        S(() => {
            if (vessel.Name() && !S.sample(pSixSearch.VesselName)) pSixSearch.VesselName(vessel.Name()!);
        });

        S(() => {
            if (vessel.CallSign() && !S.sample(pSixSearch.CallSign)) pSixSearch.CallSign(vessel.CallSign()!);
            if (vessel.CallSign() && !S.sample(lloydSearch.CallSign)) lloydSearch.CallSign(vessel.CallSign()!);
        });

        S(() => {
            if (vessel.VIN() && !S.sample(pSixSearch.VIN)) pSixSearch.VIN(vessel.VIN()!);
        });
        // End populate search terms

        // Populate selected result ids from vessel info
        S(() => lloydSearch.SelectedResultId(vessel.LloydServiceVesselId()));
        S(() => marineTrafficSearch.SelectedResultId(vessel.MarineTrafficServiceVesselId()));
        S(() => pSixSearch.SelectedResultId(vessel.PSixServiceVesselId()));
    });

    request.done(json => {
        cache.VesselServiceConfigures.update1(request.Date, json);
    });

    return {
        Model: model,
        VesselServiceSettings: vesselServiceSettings,
        LloydSearch: lloydSearch,
        MarineTrafficSearch: marineTrafficSearch,
        PSixSearch: pSixSearch,
        SelectedDuplicateVesselId: selectedDuplicateVesselId,
        GoToOverrides: () => window.location.hash = '#!vesselserviceprofile?id=' + id,
        GoBack: () => window.history.back()
    };

    function SearchModelBase<TServiceVessel extends { Id : DataSignal<number> }, TSearch, TSearchOptions>(
        url : (key : TSearchOptions) => string,
        cacheCollection : DataCacheCollection<TSearch, TSearchOptions>,
        resultModel : (result : TServiceVessel, searchKey : () => TSearchOptions) => ISearchResult<TServiceVessel>,
        resultsRef : (search : TSearch) => SDataArray<TServiceVessel>,
        serviceVesselIdRef : (vessel : Vessel) => DataSignal<number | null>
    ) {
        const isSearching = S.data(false),
            searchKey = S.data<TSearchOptions | null>(null),
            currentSearch = S(() => searchKey() && cacheCollection.update1(null, searchKey()!)),
            results = S(() => currentSearch() ?
                resultsRef(currentSearch()!)
                    .map(result => resultModel(result, () => searchKey()!))
                    // Descending sort on SearchResultWeight
                    .sort((res1, res2) => res2.SearchResultWeight() - res1.SearchResultWeight())
                : SArray<ISearchResult<TServiceVessel>>([])
            ),
            resultsIdx = S(() => uniqueIndex(results(), r => r.Model.Id())),
            selectedResultId = S.data<number | null>(null);

        // Send search request when search key set
        S(() => {
            if (searchKey() && !S.sample(isSearching)) {
                isSearching(true);
                let req = ajax.Url<TSearchOptions>(url(searchKey()!)).Get();
                req.then(json => {
                    cacheCollection.update1(req.Date, json);
                }).finally(() => isSearching(false));
            }
        });

        return {
            IsSearching: isSearching,
            Results: () => results()(),
            SelectedResultId: selectedResultId,
            SearchKey: searchKey,
            Link,
            SelectCurrentVessel,
            SelectedIdIsCurrent,
            CurrentVesselInResults
        };

        function Link() {
            const vessel = S.sample(model.Vessel);
            if (!vessel) return;
            serviceVesselIdRef(vessel)(S.sample(selectedResultId));
            ajax.Url<Vessel>(() => vessel.Url() || '', { useCache: true }).Put(vessel);
        }

        function SelectCurrentVessel() {
            const vessel = S.sample(model.Vessel);
            if (!vessel) return;
            selectedResultId(S.sample(serviceVesselIdRef(vessel)));
        }

        function SelectedIdIsCurrent() {
            const vessel = model.Vessel();
            if (!vessel) return false;
            const serviceVesselId = serviceVesselIdRef(vessel)();
            return selectedResultId() === serviceVesselId;
        }

        function CurrentVesselInResults() {
            const vessel = model.Vessel();
            if (!vessel) return false;
            const serviceVesselId = serviceVesselIdRef(vessel)() || 0;
            return !!resultsIdx()(serviceVesselId)();
        }
    }

    function LloydSearchModel() : LloydSearchModel {
        const imo = S.data<number | null>(null),
            mmsid = S.data<number | null>(null),
            //Defaulting callSign to empty string.  WebApi causes this to be seen as a null.  Adding fix to API to account for this
            callSign = S.data(''),
            searchModelBase = SearchModelBase(
                entrypoints.LloydsSearch,
                cache.LloydServiceSearches,
                LloydSearchResult,
                s => s.Results,
                v => v.LloydServiceVesselId
            );

        return Object.assign({}, searchModelBase, {
            IMO: imo,
            MMSID: mmsid,
            CallSign: callSign,
            Search,
            CanSearch: () => !searchModelBase.IsSearching() && !!(imo() || mmsid() || callSign())
        });

        function Search() {
            searchModelBase.SearchKey(S.sample(() => ({
                IMO: imo(),
                MMSID: mmsid(),
                CallSign: callSign() ? callSign() : null
            })));
        }
    }

    function MarineTrafficSearchModel() : MarineTrafficSearchModel {
        const imo = S.data<number | null>(null),
            mmsid = S.data<number | null>(null),
            searchModelBase = SearchModelBase(
                entrypoints.MarineTrafficSearch,
                cache.MarineTrafficServiceSearches,
                MarineTrafficSearchResult,
                s => s.Results,
                v => v.MarineTrafficServiceVesselId
            );

        return Object.assign({}, searchModelBase, {
            IMO: imo,
            MMSID: mmsid,
            Search,
            CanSearch: () => !searchModelBase.IsSearching() && !!(imo() || mmsid())
        });

        function Search() {
            searchModelBase.SearchKey(S.sample(() => ({
                IMO: imo(),
                MMSID: mmsid()
            })));
        }
    }

    function PSixSearchModel() : PSixSearchModel {
        const vesselName = S.data(''),
            callSign = S.data(''),
            hin = S.data(''),
            vin = S.data(''),
            searchModelBase = SearchModelBase(
                entrypoints.PSixSearch,
                cache.PSixServiceSearches,
                PSixSearchResult,
                s => s.Results,
                v => v.PSixServiceVesselId
            );

        return Object.assign({}, searchModelBase, {
            VesselName: vesselName,
            CallSign: callSign,
            HIN: hin,
            VIN: vin,
            Search,
            CanSearch: () => !searchModelBase.IsSearching() && !!(vesselName() || callSign() || hin() || vin())
        });

        function Search() {
            searchModelBase.SearchKey(S.sample(() => ({
                // TH 11/2/2016: Bugfix -- Coerce empty string to null.
                // This is b/c we pass these parameters to the server via the query string, and WebAPI parses empty query string values as null,
                // not the empty string. W/o this fix, we were getting key mismatches btwn this key and the server response whenever one of the search terms was empty,
                // leading to our search results not updating.
                VesselName: vesselName() ? vesselName() : null,
                CallSign: callSign() ? callSign() : null,
                HIN: hin() ? hin() : null,
                VIN: vin() ? vin() : null
            })));
        }
    }

    function LloydSearchResult(vessel : LloydServiceVessel, searchKey : () => LloydServiceSearchOptions) : ISearchResult<LloydServiceVessel> {
        return {
            Model: vessel,
            SearchResultWeight: () => LloydOrMarineTrafficSearchResultWeight(vessel, searchKey())
        };
    }

    function MarineTrafficSearchResult(vessel : MarineTrafficServiceVessel, searchKey : () => MarineTrafficServiceSearchOptions) : ISearchResult<MarineTrafficServiceVessel> {
        return {
            Model: vessel,
            SearchResultWeight: () => LloydOrMarineTrafficSearchResultWeight(vessel, searchKey())
        };
    }

    function PSixSearchResult(vessel : PSixServiceVessel, searchKey : () => PSixServiceSearchOptions) : ISearchResult<PSixServiceVessel> {
        return {
            Model: vessel,
            SearchResultWeight: () => PSixSearchResultWeight(vessel, searchKey())
        };
    }

    function LloydOrMarineTrafficSearchResultWeight(vessel : LloydServiceVessel | MarineTrafficServiceVessel, searchKey : LloydServiceSearchOptions | MarineTrafficServiceSearchOptions) {
        let weight = 0;
        if (searchKey.IMO   !== null && '' + searchKey.IMO   === vessel.IMO())   weight++;
        if (searchKey.MMSID !== null && '' + searchKey.MMSID === vessel.MMSID()) weight++;
        return weight;
    }

    function PSixSearchResultWeight(vessel : PSixServiceVessel, searchKey : PSixServiceSearchOptions) {
        let weight = 0;
        if (searchKey.VesselName    && vessel.Name()                     && searchKey.VesselName.toUpperCase() === vessel.Name()!.toUpperCase())                     weight++;
        if (searchKey.CallSign      && vessel.CallSign()                 && searchKey.CallSign.toUpperCase()   === vessel.CallSign()!.toUpperCase())                 weight++;
        if (searchKey.HIN           && vessel.HullIdentificationNumber() && searchKey.HIN.toUpperCase()        === vessel.HullIdentificationNumber()!.toUpperCase()) weight++;
        if (searchKey.VIN           && vessel.VIN()                      && searchKey.VIN.toUpperCase()        === vessel.VIN()!.toUpperCase())                      weight++;
        return weight;
    }
}
export type VesselServiceConfigure = ReturnType<typeof VesselServiceConfigure>;


// WEBPACK FOOTER //
// ./apps/portcall/vesselserviceconfigure/model.ts